import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, BookOpen, PenLine, Wand2, Play, Eye, Clock,
  Users, Library, Globe, Volume2, PaintBucket, Moon, Smartphone,
  ChevronRight, BookMarked, Layers, RotateCcw
} from 'lucide-react';
import Wordmark from '../components/Wordmark';
import TourSection from '../components/tour/TourSection';
import ImagePlaceholder from '../components/tour/ImagePlaceholder';

const TourPage = (): ReactElement => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'var(--st-bg-page)' }}>
      {/* Navigation Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-sm"
        style={{
          background: 'color-mix(in srgb, var(--st-bg-surface) 95%, transparent)',
          borderBottom: '1px solid var(--st-border-light)'
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <Wordmark size="md" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/home')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:bg-black/5 dark:hover:bg-white/10"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
            >
              Explore
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
                color: 'white',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Section 1: Hero */}
      <section className="py-16 md:py-24 px-4 hero-pattern">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 animate-fade-in-up"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            See Tellor in Action
          </h1>
          <p
            className="text-base md:text-lg mb-8 max-w-2xl mx-auto animate-fade-in-up"
            style={{
              fontFamily: 'Quicksand, sans-serif',
              color: 'var(--st-text-body)',
              animationDelay: '100ms'
            }}
          >
            Watch how AI transforms your ideas into beautiful illustrated storybooks
          </p>

          {/* Video Placeholder */}
          <div className="max-w-2xl mx-auto mb-8 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <ImagePlaceholder
              label="Product Demo Video (Coming Soon)"
              aspectRatio="16/9"
              variant="video"
            />
          </div>

          <button
            onClick={() => navigate('/create')}
            className="px-8 py-4 rounded-xl text-lg font-bold transition-all hover:scale-[1.02] animate-fade-in-up"
            style={{
              background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)',
              color: 'white',
              fontFamily: 'Chewy, cursive',
              animationDelay: '300ms'
            }}
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Create Your First Story
            </span>
          </button>
        </div>
      </section>

      {/* Section 2: How It Works - 3-Step Overview */}
      <section className="py-16 px-4" style={{ background: 'var(--st-bg-surface)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              How It Works
            </h2>
            <p
              className="text-sm"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              Three simple steps to your personalized storybook
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: PenLine,
                title: 'Describe',
                description: 'Tell us about your story - the characters, setting, and adventure you have in mind',
                color: 'var(--st-primary)'
              },
              {
                icon: Wand2,
                title: 'Generate',
                description: 'Our AI creates beautiful illustrations and writes engaging narratives',
                color: '#8B5CF6'
              },
              {
                icon: BookOpen,
                title: 'Read',
                description: 'Enjoy your story in our immersive 3D book viewer or download to share',
                color: '#10B981'
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="text-center rounded-xl p-6 shadow-sm transition-all hover:shadow-md hover:scale-[1.02]"
                  style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
                >
                  <div
                    className="w-10 h-10 mx-auto mb-3 rounded-full flex items-center justify-center text-lg font-bold"
                    style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)', fontFamily: 'Chewy, cursive' }}
                  >
                    {index + 1}
                  </div>
                  <div
                    className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                    style={{ background: `${step.color}15` }}
                  >
                    <Icon className="w-8 h-8" style={{ color: step.color }} />
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Story Creation Deep Dive */}
      <TourSection
        title="Craft Your Perfect Story"
        description="Our story creator gives you complete control over every aspect of your book. Choose from multiple options to make each story uniquely yours."
        features={[
          '3 age groups: Picture Books (3-5), Story Books (5-8), Chapter Books (8-12)',
          '18 story categories from Adventure to Mystery to Bedtime',
          '8 beautiful art styles including Watercolor, Storybook, and Anime',
          'Add custom characters with photo upload',
          'Smart prompt suggestions to spark creativity'
        ]}
        imagePlaceholder="CreatePage Screenshot"
        imageSrcLight="/images/tour/create-story-light.png"
        imageSrcDark="/images/tour/create-story-dark.png"
        imagePosition="right"
        background="cream"
      />

      {/* Section 4: Progressive Viewing */}
      <TourSection
        title="Watch Your Story Come to Life"
        description="Don't wait until the entire book is ready. With Progressive Viewing, you can start reading as soon as the first few pages are generated."
        features={[
          'Start reading after just 3 pages are ready',
          'Real-time page updates as more illustrations complete',
          'Progress indicator shows when book reaches "Ready to Read" milestone',
          'Seamless transition from generating to reading'
        ]}
        imagePlaceholder="GeneratingPage with Progress Bar"
        imageSrcLight="/images/tour/real-time-story-light.png"
        imageSrcDark="/images/tour/real-time-story-dark.png"
        imageObjectFit="contain"
        imagePosition="left"
        badge={{ text: 'REAL-TIME', variant: 'green' }}
        background="white"
      >
        <div className="flex items-center gap-2 mt-4">
          <Clock className="w-4 h-4" style={{ color: '#8B5CF6' }} />
          <span
            className="text-xs"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            Most stories are fully generated within 3-5 minutes
          </span>
        </div>
      </TourSection>

      {/* Section 5: 3D Book Viewer - Full Width */}
      <section
        className="py-20 px-4"
        style={{
          background: 'linear-gradient(135deg, var(--st-bg-page) 0%, var(--st-border-light) 50%, var(--st-bg-page) 100%)'
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Immersive 3D Book Experience
            </h2>
            <p
              className="text-base max-w-xl mx-auto"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
            >
              Read your stories in a beautiful, interactive 3D book viewer with realistic page-turning physics
            </p>
          </div>

          {/* Large Screenshot */}
          <div className="max-w-3xl mx-auto mb-10">
            <ImagePlaceholder
              label="3D Book Viewer Screenshot"
              aspectRatio="16/9"
              imageSrcLight="/images/tour/3d-book.png"
              imageSrcDark="/images/tour/3d-book.png"
            />
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: RotateCcw,
                title: 'Drag to Explore',
                description: 'Rotate and view your book from any angle with intuitive controls',
                color: 'var(--st-primary)'
              },
              {
                icon: BookMarked,
                title: 'Page-Turning Physics',
                description: 'Realistic page animations that feel like a real book',
                color: '#8B5CF6'
              },
              {
                icon: Volume2,
                title: 'Audio Narration',
                description: 'Listen along as pages sync with natural AI voice narration',
                color: '#3B82F6'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="text-center rounded-xl p-5 shadow-sm"
                  style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
                >
                  <div
                    className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center"
                    style={{ background: `${feature.color}15` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>
                  <h3
                    className="text-base font-bold mb-1"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-xs"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 6: Series & Character Continuity */}
      <section className="py-16 px-4 series-section-bg">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            {/* Left: Image */}
            <div className="flex-1">
              <ImagePlaceholder
                label="Series Page with Character Continuity"
                aspectRatio="4/3"
                imageSrcLight="/images/tour/series-light.png"
                imageSrcDark="/images/tour/series-dark.png"
              />
            </div>

            {/* Right: Content */}
            <div className="flex-1 space-y-4">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ background: '#E0E7FF' }}
              >
                <Library className="w-4 h-4" style={{ color: '#6366F1' }} />
                <span
                  className="text-xs font-bold tracking-wide"
                  style={{ color: '#4F46E5', fontFamily: 'Quicksand, sans-serif' }}
                >
                  PREMIUM FEATURE
                </span>
              </div>

              <h2
                className="text-2xl md:text-3xl font-bold"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                Series & Character Continuity
              </h2>

              <p
                className="text-base leading-relaxed"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}
              >
                Create recurring characters that look exactly the same across multiple books.
                Build epic adventures that span entire collections.
              </p>

              <div className="space-y-3">
                {[
                  { icon: Users, text: 'Characters maintain their appearance across all books in a series', color: '#6366F1' },
                  { icon: Globe, text: 'World-building and settings persist between stories', color: '#8B5CF6' },
                  { icon: BookOpen, text: 'Book summaries carry forward for connected narratives', color: '#10B981' },
                  { icon: Layers, text: 'Visual consistency powered by IllustrationBible technology', color: 'var(--st-primary)' }
                ].map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${feature.color}15` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: feature.color }} />
                      </div>
                      <span
                        className="text-sm pt-1"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                      >
                        {feature.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => navigate('/pricing')}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif',
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)'
                }}
              >
                <Library className="w-5 h-5" />
                Unlock Series Feature
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: More Features Grid */}
      <section className="py-16 px-4" style={{ background: 'var(--st-bg-surface)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Even More Features
            </h2>
            <p
              className="text-sm"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
            >
              Everything you need to create and enjoy personalized stories
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Volume2,
                title: 'Audio Narration',
                description: 'Natural AI voices bring stories to life',
                color: '#3B82F6',
                premium: true
              },
              {
                icon: PaintBucket,
                title: 'Storyboard Editor',
                description: 'Regenerate or edit any page illustration',
                color: 'var(--st-primary)',
                premium: false
              },
              {
                icon: Moon,
                title: 'Dark Mode',
                description: 'Easy on the eyes for bedtime reading',
                color: '#6366F1',
                premium: false
              },
              {
                icon: Smartphone,
                title: 'Mobile App',
                description: 'Read on the go (iOS & Android)',
                color: '#10B981',
                comingSoon: true
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="rounded-xl p-5 shadow-sm transition-all hover:shadow-md hover:scale-[1.02] relative"
                  style={{ background: 'var(--st-card-bg)', border: '1px solid var(--st-card-border)' }}
                >
                  {feature.premium && (
                    <span
                      className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ background: '#FEF3C7', color: '#D97706' }}
                    >
                      PREMIUM
                    </span>
                  )}
                  {feature.comingSoon && (
                    <span
                      className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ background: '#E0E7FF', color: '#4F46E5' }}
                    >
                      COMING SOON
                    </span>
                  )}
                  <div
                    className="w-12 h-12 mb-3 rounded-xl flex items-center justify-center"
                    style={{ background: `${feature.color}15` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>
                  <h3
                    className="text-base font-bold mb-1"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-xs"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 8: Final CTA */}
      <section
        className="py-16 px-4"
        style={{
          background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)'
        }}
      >
        <div className="max-w-xl mx-auto text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4 text-white"
            style={{ fontFamily: 'Chewy, cursive' }}
          >
            Ready to Create Your Story?
          </h2>
          <p
            className="text-white/90 mb-8 text-base"
            style={{ fontFamily: 'Quicksand, sans-serif' }}
          >
            Join thousands of families creating personalized illustrated storybooks.
            Start for free and bring your imagination to life.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/create')}
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl text-lg font-bold transition-all hover:scale-[1.02]"
              style={{
                background: 'var(--st-bg-surface)',
                color: 'var(--st-primary)',
                fontFamily: 'Chewy, cursive',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
              }}
            >
              Start Creating - It's Free
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-base font-semibold transition-all hover:bg-white/10"
              style={{
                background: 'transparent',
                border: '2px solid white',
                color: 'white',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              View Pricing
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center landing-footer">
        <div className="flex items-center justify-center mb-2">
          <Wordmark size="lg" variant="light" />
        </div>
        <p
          className="text-xs text-white/70 mb-1"
          style={{ fontFamily: 'Quicksand, sans-serif' }}
        >
          Create personalized illustrated storybooks for your children
        </p>
        <p
          className="text-xs text-white/50 mb-4"
          style={{ fontFamily: 'Quicksand, sans-serif' }}
        >
          A Mytho Labs Product
        </p>
        <div className="flex flex-wrap justify-center gap-4 text-xs" style={{ fontFamily: 'Quicksand, sans-serif' }}>
          <button
            onClick={() => navigate('/help')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Help & FAQ
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/contact')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Contact
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/terms')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Terms
          </button>
          <span className="text-white/30">|</span>
          <button
            onClick={() => navigate('/privacy')}
            className="text-white/70 hover:text-white transition-colors"
          >
            Privacy
          </button>
        </div>
      </footer>
    </div>
  );
};

export default TourPage;
